import React, { useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faFlask, faGraduationCap, faMicrochip, faChalkboardUser, faUsers, faCode } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import DotGrid from './DotGrid';
import '../assets/styles/Timeline.scss';

type Role = {
    title: string;
    company: string;
    location: string;
    date: string;
    description: string;
    tags: string[];
    icon: IconDefinition;
    logo?: string;
};

const roles: Role[] = [
    {
        title: "Software Engineer — Cashiering Technology",
        company: "Charles Schwab",
        location: "Austin, TX",
        date: "Jun 2026 – Present",
        description: "Building APIs for money movement on the Move Money team.",
        tags: ["Java", "API Development"],
        icon: faBriefcase,
        logo: "/logos/schwab.png",
    },
    {
        title: "Team Lead",
        company: "Texas Luminescence",
        location: "Austin, TX",
        date: "May 2025 – May 2026",
        description: "Led an 11-person team building Allocare, an AI-powered budgeting app with predictive financial coaching.",
        tags: ["Leadership", "AI", "Fintech"],
        icon: faUsers,
        logo: "/logos/texas-luminescence.jpg",
    },
    {
        title: "Fintech Foundations Program Fellow",
        company: "Visa",
        location: "Austin, TX",
        date: "Oct 2025 – Nov 2025",
        description: "Fintech innovation case studies; won 3rd place at capstone.",
        tags: ["Fintech", "Case Studies", "Capstone"],
        icon: faGraduationCap,
        logo: "/logos/visa.png",
    },
    {
        title: "Undergraduate Researcher",
        company: "Living with Robots Laboratory · Texas Robotics",
        location: "Austin, TX",
        date: "Jul 2024 – Oct 2025",
        description: "Human-robot interaction research with NLP and ASR pipelines.",
        tags: ["HRI", "NLP", "ML", "ASR"],
        icon: faFlask,
        logo: "/logos/utexas.png",
    },
    {
        title: "SWE Intern — Wire Transfer Team",
        company: "Charles Schwab",
        location: "Southlake, TX",
        date: "Jun 2025 – Aug 2025",
        description: "Built internal systems supporting wire transfers and payment operations.",
        tags: ["C#", ".NET 8", "REST APIs", "CI/CD", "Agile"],
        icon: faBriefcase,
        logo: "/logos/schwab.png",
    },
    {
        title: "Resident Assistant",
        company: "UTCS Academy for Machine Learning",
        location: "Austin, TX",
        date: "Jun 2025",
        description: "Mentored students through ML fundamentals and coordinated technical workshops.",
        tags: ["ML Mentorship", "Workshops", "Logistics"],
        icon: faChalkboardUser,
        logo: "/logos/utexas.png",
    },
    {
        title: "Full-Stack Developer",
        company: "Texas Luminescence",
        location: "Austin, TX",
        date: "Aug 2024 – May 2025",
        description: "Built TradeTorch, a stock candlestick prediction tool using a GRU-TCN ensemble trained on 10+ years of data.",
        tags: ["React", "Flask", "Python", "Deep Learning"],
        icon: faCode,
        logo: "/logos/texas-luminescence.jpg",
    },
    {
        title: "Program Assistant",
        company: "The UTCS Academy for Robotics",
        location: "Austin, TX",
        date: "Jun 2024",
        description: "Taught robotics foundations: Linux, C++, Arduino, PID control.",
        tags: ["Linux", "C++", "Arduino", "PID"],
        icon: faMicrochip,
        logo: "/logos/utexas.png",
    },
];

function Timeline() {
    const sectionRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const el = sectionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--tx', `${e.clientX - rect.left}px`);
        el.style.setProperty('--ty', `${e.clientY - rect.top}px`);
    };

    return (
        <div id="history" className="timeline-section" ref={sectionRef} onMouseMove={handleMouseMove}>
            <DotGrid />
            <div className="timeline-bg" />
            <div className="items-container">
                <h1>Career History</h1>
                <div className="timeline">
                    <div className="timeline-rail" />
                    {roles.map((role, i) => (
                        <div key={i} className="timeline-item">
                            <div className="timeline-node">
                                <FontAwesomeIcon icon={role.icon} />
                            </div>
                            <div className="timeline-card">
                                <div className="timeline-header">
                                    {role.logo && (
                                        <img
                                            src={role.logo}
                                            alt={`${role.company} logo`}
                                            className="timeline-logo"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                    <div className="timeline-heading">
                                        <h3 className="timeline-title">{role.title}</h3>
                                        <h4 className="timeline-company">{role.company} · <span>{role.location}</span></h4>
                                    </div>
                                    <span className="timeline-date">{role.date}</span>
                                </div>
                                <p className="timeline-desc">{role.description}</p>
                                <div className="timeline-tags">
                                    {role.tags.map((t) => <span key={t} className="tl-tag">{t}</span>)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Timeline;
